/* ===========================================================================
   Appstle bundle -> add_to_cart

   Appstle's "build a box" adds the Foundation bundle by calling the Ajax
   Cart API directly:

     GET  /cart/clear.js
     POST /cart/add.js   {"items":[{"id":53678275133773, "selling_plan":...,
                          "properties":{"_appstle_bundles_type": ...}}]}
     then it navigates to /cart

   Because that POST is a raw fetch() rather than a cart interaction Shopify
   instruments, Shopify never emits its `product_added_to_cart` standard
   event. The Google & YouTube channel pixel subscribes to that event, so the
   bundle add reaches neither GA4 nor Google Ads. Measured 2026-09-20: adding
   the $95 Foundation bundle produced zero add_to_cart anywhere, while a
   single-item subscription add (Deep Rest at $31.50) reported correctly.

   That mattered enough to fix: Foundation is the highest-value item in the
   catalogue, and Add to cart / Begin checkout are now the primary conversion
   goals in Google Ads — so without this, bidding would learn to avoid the
   customers worth the most.

   WHY IT IS SPLIT ACROSS A NAVIGATION
   The obvious shape — read the /cart/add.js response, then report — loses the
   race. Appstle navigates to /cart immediately, and the first attempt at this
   fired nothing at all because the document unloaded before the cloned JSON
   body resolved. So the two halves are separated:

     1. mark   - synchronous, on the way out. Inspects the request body, which
                 already names Appstle, and sets a sessionStorage flag. No
                 awaiting, so nothing to lose to the unload.
     2. report - on the next page load (or shortly after, if no navigation
                 happened). Reads /cart.js for real prices and fires once.

   The flag is cleared before the event is sent, so the delayed path and the
   load path cannot both report the same add.

   Normal and single-item subscription adds already fire the standard event
   and are deliberately skipped, so nothing is double counted.

   The page-level gtag reaches Google Ads but has no GA4 config of its own
   (GA4 events come from the sandboxed channel pixel), so we register GA4
   once with send_page_view:false — that adds the measurement ID as a
   destination without emitting a second page_view. Verified: one GA4 hit,
   tid G-9RJZCQ4VPR, and zero page_view leakage.

   Consent needs no handling here. gtag reads the Consent Mode state Shopify
   publishes, so this stays silent until the visitor allows it.

   Appstle uses fetch today. If they move to XMLHttpRequest the mark step
   stops firing silently, so re-check after any Appstle update.
   ========================================================================= */

(function () {
  'use strict';

  var GA4_MEASUREMENT_ID = 'G-9RJZCQ4VPR';
  var PENDING_KEY = 'mos:appstle-bundle-pending';
  var ga4Registered = false;

  function gtagAvailable() {
    return typeof window.gtag === 'function';
  }

  function registerGa4Once() {
    if (ga4Registered || !gtagAvailable()) return;
    window.gtag('config', GA4_MEASUREMENT_ID, { send_page_view: false });
    ga4Registered = true;
  }

  /* gtag arrives with {{ content_for_header }}, after this file runs, so poll
     rather than assume. Gives up after ~10s: past that the visitor has almost
     certainly declined consent and gtag is never coming. */
  function whenGtagReady(callback) {
    if (gtagAvailable()) { callback(); return; }
    var attempts = 0;
    var timer = window.setInterval(function () {
      if (gtagAvailable()) {
        window.clearInterval(timer);
        callback();
      } else if (++attempts > 40) {
        window.clearInterval(timer);
      }
    }, 250);
  }

  function readFlag() {
    try { return window.sessionStorage.getItem(PENDING_KEY) === '1'; } catch (e) { return false; }
  }

  function writeFlag(on) {
    try {
      if (on) window.sessionStorage.setItem(PENDING_KEY, '1');
      else window.sessionStorage.removeItem(PENDING_KEY);
    } catch (e) { /* private mode: the add simply goes unreported */ }
  }

  /* Appstle writes several properties on the bundle line; the two stable
     families are `_appstle-bb-*` and `_appstle_bundles_*`. Matching on the
     family rather than one exact key survives their key renames. */
  function isAppstleBundleLine(line) {
    var properties = line && line.properties;
    if (!properties || typeof properties !== 'object') return false;
    return Object.keys(properties).some(function (key) {
      return key.indexOf('appstle-bb') !== -1 || key.indexOf('appstle_bundles') !== -1;
    });
  }

  function toMoney(cents) {
    return typeof cents === 'number' ? cents / 100 : 0;
  }

  function reportFromCart() {
    if (!readFlag()) return;

    /* Clear first. If the fetch below fails we lose one event, which is far
       better than reporting the same add twice from two entry points. */
    writeFlag(false);

    window.fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var lines = (cart && cart.items ? cart.items : []).filter(isAppstleBundleLine);
        if (!lines.length) return;

        var total = 0;
        var items = lines.map(function (line) {
          var linePrice = line.final_line_price != null ? line.final_line_price : line.line_price;
          var unitPrice = line.final_price != null ? line.final_price : line.price;
          total += toMoney(linePrice);

          return {
            item_id: line.sku || String(line.product_id || line.id || ''),
            item_name: line.product_title || line.title || 'Bundle',
            item_variant: line.variant_title || undefined,
            price: toMoney(unitPrice),
            quantity: line.quantity || 1
          };
        });

        /* Wait for gtag rather than dropping the event if the visitor has
           only just accepted consent. registerGa4Once already ran at load,
           so by now the GA4 destination is registered and the event lands on
           both GA4 and Google Ads — sending the event in the same tick as the
           config does not, which cost the first version its GA4 hit. */
        whenGtagReady(function () {
          registerGa4Once();
          window.gtag('event', 'add_to_cart', {
            currency: (cart && cart.currency) || 'USD',
            value: total,
            items: items
          });
        });
      })['catch'](function () { /* analytics must never break the cart */ });
  }

  /* ---- 1. mark, synchronously, before any navigation can interrupt ---- */
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
          var body = init && typeof init.body === 'string' ? init.body : '';
          if (body.indexOf('appstle') !== -1) writeFlag(true);
        }
      } catch (error) { /* noop */ }
      return nativeFetch.apply(this, arguments);
    };
  }

  /* Register GA4 as early as gtag allows, on every page rather than only when
     reporting, so the destination is warm long before an event needs it. */
  whenGtagReady(registerGa4Once);

  /* ---- 2. report, whether or not Appstle navigated ---- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reportFromCart);
  } else {
    reportFromCart();
  }

  /* Covers the case where Appstle opens a drawer instead of navigating: the
     flag is set but no new document arrives to pick it up. */
  window.setTimeout(reportFromCart, 2500);
})();
